import React, { useState } from 'react';
import {
  CreditCardIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  PlusIcon,
  MinusIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addYears, differenceInDays } from 'date-fns';
import axiosInstance from '../../utils/axios';

const PaymentInformation = ({ user, onUpdate }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [extensionPeriod, setExtensionPeriod] = useState(1);
  const [paymentHistory, setPaymentHistory] = useState(user?.billing?.paymentHistory || []);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  // Parse initial plan from either new fields or old currentPlan
  const getInitialPlan = () => {
    const { planType, country, currentPlan } = user?.subscription || {};

    if (planType && country) {
      return { planType, country };
    }

    // Parse from currentPlan if available (e.g., "single_cad")
    if (currentPlan) {
      const parts = currentPlan.split('_');
      if (parts.length === 2) {
        return {
          planType: parts[0], // 'single' or 'multi'
          country: parts[1].toUpperCase() === 'CAD' ? 'CA' : 'US'
        };
      }
    }

    // Default values
    return {
      planType: 'single',
      country: 'US'
    };
  };

  const [selectedPlan, setSelectedPlan] = useState(getInitialPlan());

  // On screen an account is either in or out, so the stored value collapses to
  // one of two. A free year counts as active because it grants the same access
  // and the customer's own app calls it active too.
  const getStatusDetails = (status) =>
    status === 'active' || status === 'trial'
      ? { color: 'green', icon: CheckCircleIcon, text: 'Active' }
      : { color: 'gray', icon: XCircleIcon, text: 'Inactive' };

  const subscription = user?.subscription || {};
  const statusDetails = getStatusDetails(subscription.status);
  const StatusIcon = statusDetails.icon;

  // Calculate days until expiry
  const getDaysUntilExpiry = () => {
    if (!subscription.endDate) return null;
    const days = differenceInDays(new Date(subscription.endDate), new Date());
    return days;
  };

  const daysUntilExpiry = getDaysUntilExpiry();

  // Get plan display name
  const getPlanDisplayName = () => {
    const { planType, country, status, currentPlan } = subscription;

    // Check if we have the old format (currentPlan like "single_cad")
    if (!planType && !country && currentPlan) {
      // Parse the old format
      const parts = currentPlan.split('_');
      if (parts.length === 2) {
        const deviceText = parts[0] === 'single' ? 'Single Device' : 'Multiple Devices';
        const countryText = parts[1].toUpperCase() === 'US' ? 'United States' : 'Canada';
        return `${deviceText} - ${countryText}`;
      }
    }

    // If no plan type or country set, check if subscription is active
    if (!planType && !country) {
      if (status === 'active' || status === 'trial') {
        return 'Plan Info Missing';
      }
      return 'No Plan Selected';
    }

    if (!planType || !country) return 'Incomplete Plan Info';

    const deviceText = planType === 'single' ? 'Single Device' : 'Multiple Devices';
    const countryText = country === 'US' ? 'United States' : 'Canada';
    return `${deviceText} - ${countryText}`;
  };

  // Handle extending a customer's annual access
  const handleExtendSubscription = async () => {
    setIsUpdating(true);
    try {
      const newEndDate = subscription.endDate
        ? addYears(new Date(subscription.endDate), extensionPeriod)
        : addYears(new Date(), extensionPeriod);

      await axiosInstance.patch(`/v1/users/${user._id}/subscription`, {
        endDate: newEndDate,
        status: 'active'
      });

      if (onUpdate) {
        onUpdate({
          ...user,
          subscription: {
            ...subscription,
            endDate: newEndDate,
            status: 'active'
          }
        });
      }
      setShowExtendModal(false);
    } catch (error) {
      console.error('Error extending subscription:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    setIsUpdating(true);
    try {
      const updateData = { status: newStatus };

      // Set appropriate dates based on status change
      if (newStatus === 'active' && subscription.status !== 'active') {
        updateData.startDate = new Date();
        if (!subscription.endDate) {
          updateData.endDate = addYears(new Date(), 1);
        }
      }

      await axiosInstance.patch(`/v1/users/${user._id}/subscription`, updateData);

      if (onUpdate) {
        onUpdate({
          ...user,
          subscription: {
            ...subscription,
            ...updateData
          }
        });
      }
      setShowStatusModal(false);
    } catch (error) {
      console.error('Error updating subscription status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle plan update - admin can change everything
  const handlePlanUpdate = async () => {
    setIsUpdating(true);
    try {
      await axiosInstance.patch(`/v1/users/${user._id}/subscription`, {
        planType: selectedPlan.planType,
        country: selectedPlan.country,
        adminSetPlan: true  // Mark that admin set this
      });

      if (onUpdate) {
        onUpdate({
          ...user,
          subscription: {
            ...subscription,
            planType: selectedPlan.planType,
            country: selectedPlan.country,
            adminSetPlan: true
          }
        });
      }
      setShowPlanModal(false);
    } catch (error) {
      console.error('Error updating plan:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Annual access overview */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Annual Access Details</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Status */}
            <div className="space-y-2">
              <label className="text-sm text-gray-500">Status</label>
              <div className="flex items-center gap-3">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-${statusDetails.color}-100`}>
                  <StatusIcon className={`w-4 h-4 text-${statusDetails.color}-600`} />
                  <span className={`text-sm font-medium text-${statusDetails.color}-700`}>
                    {statusDetails.text}
                  </span>
                </div>
                <button
                  onClick={() => setShowStatusModal(true)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Change
                </button>
              </div>
            </div>

            {/* Current Plan */}
            <div className="space-y-2">
              <label className="text-sm text-gray-500">Current Plan</label>
              <div className="flex items-center gap-2">
                <CreditCardIcon className="w-5 h-5 text-gray-400" />
                <span className="text-gray-900 font-medium">
                  {getPlanDisplayName()}
                </span>
                {subscription.adminSetPlan && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <ShieldCheckIcon className="w-3 h-3" />
                    Admin Set
                  </span>
                )}
              </div>
            </div>

            {/* Access period */}
            {subscription.startDate && (
              <div className="space-y-2">
                <label className="text-sm text-gray-500">Start Date</label>
                <div className="flex items-center gap-2">
                  <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-900">
                    {format(new Date(subscription.startDate), 'MMM dd, yyyy')}
                  </span>
                </div>
              </div>
            )}

            {/* End Date */}
            {subscription.endDate && (
              <div className="space-y-2">
                <label className="text-sm text-gray-500">End Date</label>
                <div className="flex items-center gap-2">
                  <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-900">
                    {format(new Date(subscription.endDate), 'MMM dd, yyyy')}
                  </span>
                  {daysUntilExpiry !== null && (
                    <span className={`text-sm ${
                      daysUntilExpiry <= 30 ? 'text-red-600' :
                      daysUntilExpiry <= 60 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      ({daysUntilExpiry} days {daysUntilExpiry >= 0 ? 'remaining' : 'overdue'})
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Stripe Customer ID */}
            {user.stripeCustomerId && (
              <div className="space-y-2">
                <label className="text-sm text-gray-500">Stripe Customer ID</label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-900 font-mono text-sm">{user.stripeCustomerId}</span>
                </div>
              </div>
            )}

            {/* Last Payment Amount */}
            {subscription.lastPaymentAmount && (
              <div className="space-y-2">
                <label className="text-sm text-gray-500">Last Payment</label>
                <div className="flex items-center gap-2">
                  <CurrencyDollarIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-900 font-medium">
                    ${subscription.lastPaymentAmount}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Deactivation Information */}
          {subscription.deactivatedAt && subscription.deactivatedBy && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-red-900 mb-2">Annual Access Deactivated</h4>
                  <div className="space-y-1 text-sm text-red-700">
                    <p>
                      <span className="font-medium">Deactivated by:</span> {subscription.deactivatedBy.firstName} {subscription.deactivatedBy.lastName}
                      {subscription.deactivatedBy.email && (
                        <span className="text-red-600"> ({subscription.deactivatedBy.email})</span>
                      )}
                    </p>
                    <p>
                      <span className="font-medium">Deactivated on:</span> {format(new Date(subscription.deactivatedAt), 'MMM dd, yyyy HH:mm')}
                    </p>
                    <p className="text-xs text-red-600 mt-2">
                      This account's annual access was ended by an administrator and must be reactivated before the customer can use the app again.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 flex gap-3">
            <button
              onClick={() => setShowPlanModal(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <CreditCardIcon className="w-4 h-4" />
              Change Device Plan
            </button>

            <button
              onClick={() => setShowExtendModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <PlusIcon className="w-4 h-4" />
              Extend Annual Access
            </button>

            <button
              onClick={() => setShowPaymentHistory(!showPaymentHistory)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <DocumentTextIcon className="w-4 h-4" />
              Payment History
            </button>
          </div>
        </div>
      </div>

      {/* Payment History */}
      <AnimatePresence>
        {showPaymentHistory && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h3>

              {paymentHistory.length > 0 ? (
                <div className="space-y-3">
                  {paymentHistory.map((payment, index) => (
                    <div key={index} className="flex items-center justify-between py-3 border-b last:border-0">
                      <div>
                        <p className="text-gray-900 font-medium">{payment.description || 'Annual Access'}</p>
                        <p className="text-sm text-gray-500">
                          {format(new Date(payment.date), 'MMM dd, yyyy')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-900 font-medium">
                          {payment.currency === 'cad' ? 'CA$' : '$'}{payment.amount}
                        </p>
                        <p className={`text-sm ${
                          payment.status === 'succeeded' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {payment.status}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No payment history available</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Extend annual access modal */}
      <AnimatePresence>
        {showExtendModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowExtendModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Extend Annual Access</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Extension Period
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setExtensionPeriod(Math.max(1, extensionPeriod - 1))}
                      className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <MinusIcon className="w-4 h-4" />
                    </button>
                    <span className="text-lg font-semibold w-20 text-center">
                      {extensionPeriod} {extensionPeriod === 1 ? 'Year' : 'Years'}
                    </span>
                    <button
                      onClick={() => setExtensionPeriod(extensionPeriod + 1)}
                      className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <PlusIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-blue-900">
                    New end date will be:{' '}
                    <span className="font-semibold">
                      {format(
                        subscription.endDate
                          ? addYears(new Date(subscription.endDate), extensionPeriod)
                          : addYears(new Date(), extensionPeriod),
                        'MMM dd, yyyy'
                      )}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowExtendModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtendSubscription}
                  disabled={isUpdating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isUpdating ? 'Extending...' : 'Extend Annual Access'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status Change Modal */}
      <AnimatePresence>
        {showStatusModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowStatusModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Change Annual Access Status</h3>

              {/* Status Information Guide */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <InformationCircleIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 mb-1">Access Control Information:</p>
                    <div className="space-y-1 text-blue-700">
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                        <span><strong>Active</strong>: Full account access allowed</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <XCircleIcon className="w-4 h-4 text-red-600" />
                        <span><strong>Inactive</strong>: No account access</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {['active', 'inactive'].map((status) => {
                  const details = getStatusDetails(status);
                  const Icon = details.icon;
                  return (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      disabled={subscription.status === status}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        subscription.status === status
                          ? 'bg-gray-50 border-gray-300 cursor-not-allowed opacity-50'
                          : 'hover:bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className={`p-2 rounded-full bg-${details.color}-100`}>
                        <Icon className={`w-5 h-5 text-${details.color}-600`} />
                      </div>
                      <div className="text-left flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{details.text}</p>
                          {(status === 'active' || status === 'trial') ? (
                            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                              Access Allowed
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                              No Access
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {status === 'active' && 'User has full access to their account'}
                          {status === 'inactive' && 'Account access is disabled'}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Change Plan Modal - Admin can change everything */}
      <AnimatePresence>
        {showPlanModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPlanModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Annual Access Plan</h3>

              <div className="space-y-4">
                {/* Admin Override Notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-amber-900 font-medium">Admin Override</p>
                      <p className="text-xs text-amber-700">
                        You are manually setting the plan. This will be marked as admin-set and respected on renewal.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Device Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Device Plan
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setSelectedPlan({ ...selectedPlan, planType: 'single' })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        selectedPlan.planType === 'single'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <CreditCardIcon className="w-6 h-6 mx-auto mb-1 text-gray-600" />
                        <p className="font-medium text-gray-900">Single Device</p>
                        <p className="text-xs text-gray-500 mt-1">One device license</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setSelectedPlan({ ...selectedPlan, planType: 'multi' })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        selectedPlan.planType === 'multi'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <CreditCardIcon className="w-6 h-6 mx-auto mb-1 text-gray-600" />
                        <p className="font-medium text-gray-900">Multiple Devices</p>
                        <p className="text-xs text-gray-500 mt-1">Multi-device license</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Country/Currency Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Country/Currency
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setSelectedPlan({ ...selectedPlan, country: 'US' })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        selectedPlan.country === 'US'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <span className="text-2xl mb-1">🇺🇸</span>
                        <p className="font-medium text-gray-900">United States</p>
                        <p className="text-xs text-gray-500 mt-1">USD Pricing</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setSelectedPlan({ ...selectedPlan, country: 'CA' })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        selectedPlan.country === 'CA'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <span className="text-2xl mb-1">🇨🇦</span>
                        <p className="font-medium text-gray-900">Canada</p>
                        <p className="text-xs text-gray-500 mt-1">CAD Pricing</p>
                      </div>
                    </button>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-sm text-purple-900">
                    Selected Plan: <span className="font-semibold">
                      {selectedPlan.planType === 'single' ? 'Single Device' : 'Multiple Devices'} -
                      {selectedPlan.country === 'US' ? ' United States (USD)' : ' Canada (CAD)'}
                    </span>
                  </p>
                  <p className="text-xs text-purple-700 mt-1">
                    This will override the user's automatic plan selection.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePlanUpdate}
                  disabled={isUpdating}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {isUpdating ? 'Updating...' : 'Set Plan'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PaymentInformation;